"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronRightIcon } from "lucide-react";
import type { ReactNode } from "react";
import { LoadingPage } from "@/components/loading-page";
import { Skeleton } from "@/components/ui/skeleton";
import { pageTitleClassName } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { SidebarTrigger } from "./ui/sidebar";
interface PageWrapperProps {
  title: string;
  children: ReactNode;
  /**
   * Optional back control rendered in the title row (after the sidebar trigger,
   * before the page title). Use for "leave this screen" navigation — not for CTAs.
   */
  back?: ReactNode;
  actions?: ReactNode;
  /**
   * Ancestor trail rendered on the **same line** as the title, before it
   * (e.g. `Projetos` › `PROJ-0003`). Prefer `PageBreadcrumbs` for parent segments.
   */
  breadcrumb?: ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "wizard" | "full";
  className?: string;
}
const MAX_WIDTH_CLASSES: Record<NonNullable<PageWrapperProps["maxWidth"]>, string> = {
  sm: "max-w-3xl",
  md: "max-w-5xl",
  lg: "max-w-6xl",
  xl: "max-w-7xl",
  "2xl": "max-w-[1360px]",
  wizard: "max-w-[1400px]",
  full: "max-w-none"
};
export function PageWrapper(t0) {
  const $ = _c(16);
  const {
    children,
    title,
    back,
    actions,
    breadcrumb,
    maxWidth: t1,
    className
  } = t0;
  const maxWidth = t1 === undefined ? "2xl" : t1;
  const t2 = MAX_WIDTH_CLASSES[maxWidth];
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("mx-auto w-full p-4 pb-16 text-xs/relaxed sm:px-6 sm:pt-6 lg:px-8", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== back) {
    t5 = back ? <div className="shrink-0" data-testid="page-back">{back}</div> : null;
    $[6] = back;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== breadcrumb || $[9] !== title) {
    t6 = breadcrumb ? <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm sm:text-base" data-testid="page-title-trail"><div className="hidden min-w-0 shrink items-center sm:flex">{breadcrumb}</div><ChevronRightIcon aria-hidden="true" className="text-muted-foreground hidden size-3.5 shrink-0 sm:block" /><h1 aria-current="page" className={cn(pageTitleClassName, "min-w-0")}>{title}</h1></nav> : <div className="min-w-0 flex-1" data-testid="page-title-trail"><h1 className={pageTitleClassName}>{title}</h1></div>;
    $[8] = breadcrumb;
    $[9] = title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== actions || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <PageFrame actions={actions} body={t4}>{t5}{t6}</PageFrame>;
    $[11] = actions;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

/**
 * Route-level loading state for app pages. Keeps the header row in place so the
 * page does not jump when the real `PageWrapper` resolves.
 */
export function PageWrapperLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PageFrame body={<LoadingPage />}><Skeleton className="h-5 w-32" /></PageFrame>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Shared header row + scroll area. `children` is the title area after the sidebar trigger. */
function PageFrame(t0) {
  const $ = _c(13);
  const {
    children,
    actions,
    body
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SidebarTrigger className="shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== actions) {
    t3 = actions && <div className="flex shrink-0 flex-wrap items-center justify-end gap-2" data-testid="page-actions">{actions}</div>;
    $[3] = actions;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <header className="bg-sidebar shrink-0 border-b p-4 lg:px-6" data-testid="page-header"><div className="flex items-center justify-between gap-3">{t2}{t3}</div></header>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== body) {
    t5 = <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">{body}</div>;
    $[8] = body;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="bg-background flex min-h-0 flex-1 flex-col">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}